import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap } from '../api/client.js';
import { SecondFactorCard } from '../components/SecondFactorCard.js';
import { ReportBrandingCard } from '../components/ReportBranding.js';
import { Button, Card, Loading, Notice, SelectField, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { LANGUAGES, setLanguage, toLanguage } from '../i18n.js';
import { PrivacyCards } from './Privacy.js';
import { ME_KEY, useMe } from '../session.js';

const BUSINESS_TYPES = ['b2c_shop', 'b2c_services', 'b2b', 'info_only'] as const;
const COMPANY_SIZES = ['solo', 'micro', 'small', 'medium'] as const;

function timeZones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone').filter((z) => z.startsWith('Europe/'));
  } catch {
    return ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich'];
  }
}

/** S12 "Konto" (account.html): details, password, report logo, export and deletion. */
export function Account() {
  const { t } = useTranslation();
  const me = useMe();
  const client = useQueryClient();
  const [form, setForm] = useState({
    fullName: '',
    name: '',
    timezone: 'Europe/Berlin',
    locale: 'de',
    businessType: '',
    companySize: '',
  });
  useEffect(() => {
    if (!me.data) return;
    setForm({
      fullName: me.data.user.fullName ?? '',
      name: me.data.account.name,
      timezone: me.data.account.timezone,
      locale: me.data.user.locale ?? me.data.account.locale,
      businessType: me.data.account.businessType ?? '',
      companySize: me.data.account.companySize ?? '',
    });
  }, [me.data]);
  const save = useMutation({
    mutationFn: async () => {
      const locale = toLanguage(form.locale);
      await unwrap(api.PATCH('/me', { body: { fullName: form.fullName.trim() || null, locale } }));
      await unwrap(
        api.PATCH('/account', {
          body: {
            name: form.name.trim(),
            timezone: form.timezone,
            locale,
            businessType: (form.businessType || null) as (typeof BUSINESS_TYPES)[number] | null,
            companySize: (form.companySize || null) as (typeof COMPANY_SIZES)[number] | null,
          },
        }),
      );
      setLanguage(locale);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ME_KEY }),
  });

  const [passwords, setPasswords] = useState({ current: '', next: '' });
  const change = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/me/change-password', {
          body: { currentPassword: passwords.current, newPassword: passwords.next },
        }),
      ),
    onSuccess: () => setPasswords({ current: '', next: '' }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const changeError = change.error instanceof ApiError ? errorMessage(change.error, t) : undefined;
  return (
    <div className="page">
      <div className="page-title">
        <h1>{t('account.title')}</h1>
      </div>
      {!me.data ? (
        <Loading />
      ) : (
        <div className="acct">
          <Card>
            <h2>{t('account.profile')}</h2>
            {/* What the customer names in support instead of the e-mail (SP-D15). */}
            <p className="hint">
              {t('account.customerNo')} <b className="mono">{me.data.account.customerNo}</b> ·{' '}
              {t('account.customerNoHint')}
            </p>
            <form onSubmit={submit}>
              <div className="fields">
                <TextField
                  label={t('account.fullName')}
                  autoComplete="name"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                />
                <TextField
                  label={t('account.companyName')}
                  autoComplete="organization"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="fields">
                {/* Read only (decision 2 of SP-UX10.6): it cannot be changed, here or by us. */}
                <TextField
                  label={t('account.email')}
                  type="email"
                  readOnly
                  hint={t('account.emailHint')}
                  value={me.data.user.email}
                />
                <SelectField
                  label={t('account.timezone')}
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                >
                  {timeZones().map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </SelectField>
              </div>
              <div className="fields3">
                <SelectField
                  label={t('account.language')}
                  value={form.locale}
                  onChange={(e) => setForm({ ...form, locale: e.target.value })}
                >
                  {LANGUAGES.map((l) => (
                    <option key={l} value={l} lang={l}>
                      {t(`lang.${l}`)}
                    </option>
                  ))}
                </SelectField>
                <SelectField
                  label={t('account.businessType')}
                  value={form.businessType}
                  onChange={(e) => setForm({ ...form, businessType: e.target.value })}
                >
                  <option value="">{t('account.businessTypes.none')}</option>
                  {BUSINESS_TYPES.map((b) => (
                    <option key={b} value={b}>
                      {t(`account.businessTypes.${b}`)}
                    </option>
                  ))}
                </SelectField>
                <SelectField
                  label={t('account.companySize')}
                  value={form.companySize}
                  onChange={(e) => setForm({ ...form, companySize: e.target.value })}
                >
                  <option value="">{t('account.companySizes.none')}</option>
                  {COMPANY_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {t(`account.companySizes.${s}`)}
                    </option>
                  ))}
                </SelectField>
              </div>
              {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
              <div className="acts">
                <Button type="submit" busy={save.isPending}>
                  {t('app.save')}
                </Button>
                {save.isSuccess ? (
                  <span role="status" className="hint">
                    {t('app.saved')}
                  </span>
                ) : null}
              </div>
            </form>
          </Card>

          <Card>
            <h2>{t('account.passwordTitle')}</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                change.mutate();
              }}
            >
              <div className="fields">
                <TextField
                  label={t('account.currentPassword')}
                  type="password"
                  autoComplete="current-password"
                  value={passwords.current}
                  onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  required
                />
                <TextField
                  label={t('account.newPassword')}
                  type="password"
                  autoComplete="new-password"
                  hint={t('auth.register.passwordHint')}
                  value={passwords.next}
                  onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
                  error={changeError}
                  required
                />
              </div>
              <div className="acts">
                <Button type="submit" variant="secondary" busy={change.isPending}>
                  {t('account.changePassword')}
                </Button>
              </div>
              {change.isSuccess ? (
                <Notice kind="success" title={t('account.passwordChanged')}>
                  {t('account.passwordChangedText')}
                </Notice>
              ) : null}
            </form>
          </Card>

          <SecondFactorCard />
          <ReportBrandingCard />
          <PrivacyCards />
        </div>
      )}
    </div>
  );
}
